import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  Button,
  Avatar,
  Spinner,
  Badge,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  ArrowBackIcon, 
  DownloadIcon, 
  ChevronDownIcon, 
  InfoIcon,
} from '@chakra-ui/icons';
import AuthContext from '../context/AuthContext';
import { formatMessageHtml } from '../utils/formatMessage';
import { sessionService } from '../services/api';

function SessionView() {
  const { sessionId } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // Colors for styling
  const userBubbleColor = useColorModeValue('brand.500', 'brand.400');
  const characterBubbleColor = useColorModeValue('gray.200', 'gray.600');
  const userTextColor = useColorModeValue('white', 'white');
  const characterTextColor = useColorModeValue('black', 'white');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    fetchSession();
  }, [sessionId, token]);

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSession = async () => {
    try {
      setIsLoading(true);
      const data = await sessionService.getPublicSession(sessionId);
      setSession(data);
    } catch (error) {
      console.error('Error fetching session:', error);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const exportToTxt = () => {
    if (!session) return;
    
    const characterName = session.characterId.character_profile?.character_name || 'Character';
    let content = `Chat Session: ${session.name}\nCharacter: ${characterName}\n\n`;
    
    session.messages.forEach(msg => {
      const label = msg.role === 'user' ? 'User:' : `${characterName}:`;
      content += `${label}\n${msg.content}\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${session.name.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="70vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!session) {
    return (
      <Box textAlign="center" py={10}>
        <Heading size="lg">Session not found</Heading>
        <Button mt={4} onClick={() => navigate('/')} colorScheme="brand">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const characterName = session.characterId.character_profile?.character_name || 'Character';

  return (
    <Box bg={bgColor} minH="calc(100vh - 180px)" pb={8}>
      <Container maxW="container.xl" pt={4}>
        <Flex justify="space-between" align="center" mb={4}>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<ArrowBackIcon />}
            colorScheme="brand"
            variant="outline"
          >
            Back
          </Button>
          
          <Flex align="center">
            <Heading size="md" noOfLines={1} textAlign="center" mx={4}>
              {session.name} - {characterName}
            </Heading>
            <Badge colorScheme="blue" ml={2}>Read Only</Badge>
          </Flex>
          
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="brand">
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem 
                icon={<InfoIcon />}
                onClick={() => navigate(`/view-character/${session.characterId._id}`)}
              >
                View Character Profile
              </MenuItem>
              <MenuItem icon={<DownloadIcon />} onClick={exportToTxt}>
                Export to TXT
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        <Box
          bg={useColorModeValue('white', 'gray.700')}
          borderRadius="lg"
          boxShadow="md"
          p={4}
          height="calc(100vh - 200px)"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-gray-300)',
              ...useColorModeValue({}, { background: 'var(--chakra-colors-gray-600)' }),
              borderRadius: '24px',
            },
          }}
        >
          {session.messages.length > 0 ? (
            <VStack spacing={4} align="stretch">
              {session.messages.map((msg, index) => (
                <Flex
                  key={index}
                  justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                >
                  <Box
                    maxW={{ base: '85%', md: '70%' }}
                    p={3}
                    borderRadius="lg"
                    bg={msg.role === 'user' ? userBubbleColor : characterBubbleColor}
                    color={msg.role === 'user' ? userTextColor : characterTextColor}
                    position="relative"
                    opacity={0.9}
                  >
                    <Flex align="center" mb={1}>
                      {msg.role !== 'user' && (
                        <Avatar
                          size="xs"
                          name={characterName}
                          src={session.characterId.imageData ? `/api/characters/${session.characterId._id}/image` : null}
                          mr={2}
                        />
                      )}
                      <Text fontWeight="bold" fontSize="sm">
                        {msg.role === 'user' ? 'User' : characterName}
                      </Text>
                      {msg.timestamp && (
                        <Text fontSize="xs" color={msg.role === 'user' ? 'white' : 'gray.500'} ml="auto">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </Text>
                      )}
                    </Flex>
                    
                    <Text
                      whiteSpace="pre-wrap"
                      dangerouslySetInnerHTML={{ __html: formatMessageHtml(msg.content) }}
                    />
                  </Box>
                </Flex>
              ))}
              <div ref={messagesEndRef} />
            </VStack>
          ) : (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              No messages in this session yet.
            </Alert>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default SessionView;